export namespace PropUtils {
  /**
   * Creates dropdown options from an array of enum values
   * @param enumValues Array of enum values to convert to dropdown options
   * @param enumObject Optional enum object to look up display names
   * @returns Array of options with name and value properties. If enumObject is provided,
   * the name will be the enum key, otherwise it will be the enum value
   */
  export function createOptionsFromEnumValues<A extends string, B extends object>(
    enumValues: A[],
    enumObject?: B,
  ) {
    if (!enumValues) return [];
    return enumValues.map((value) => ({
      name: enumObject ? getEnumKeyByValue(enumObject, value).toString() : value,
      value: value,
    }));
  }
  /**
   * Creates dropdown options from an enum object
   * @param enumObject The enum object to convert to dropdown options
   * @param useKeyAsName Optional boolean to indicate if the enum key needs to be used as name
   * @returns Array of options with name and value properties
   */
  export function createOptionsFromEnum<T extends object>(enumObject: T, useKeyAsName?: boolean) {
    if (!enumObject) return [];
    return Object.entries(enumObject)
      .filter(([key]) => isNaN(Number(key)))
      .map(([key, enumValue]) => {
        const value = typeof enumValue === 'number' ? String(enumValue) : (enumValue as string);
        return {
          name: useKeyAsName ? key : value,
          value,
        };
      });
  }
  /**
   * Removes underscores to each option key
   */
  export function getOptionsWithUnderscoresRemoved(options: { name: string; value: string }[]) {
    if (!options) return [];
    return options.map((option) => ({
      name: option.name.split('_').join(' '),
      value: option.value,
    }));
  }

  /**
   * Gets the enum key corresponding to a given value from an enum object
   * @param enumObject The enum object to search in
   * @param value The value to find the key for
   * @returns The key as a string, or empty string if not found
   */
  function getEnumKeyByValue<T extends object>(enumObject: T, value: string): string {
    const key =
      Object.keys(enumObject).find((key) => enumObject[key as keyof typeof enumObject] === value) ??
      '';
    return key;
  }
}
